import {
  ANIMAL_LABELS,
  ANIMALS,
  PRODUCT_KIND_LABELS,
  PRODUCT_KINDS,
  productSchema,
  UNIT_TYPE_LABELS,
  VAT_RATE_LABELS,
  VAT_RATES,
  type Animal,
  type CategoryDTO,
  type ProductDTO,
  type ProductKind,
  type UnitType,
  type VatRate,
} from '@bifeai/shared';
import { Button, Callout, Dialog, Field, Input, Select, Switch } from '@bifeai/ui';
import { useState, type FormEvent } from 'react';
import { api, errorMessage, isApiError } from '../../lib/api';
import { uploadProductPhoto, zodFieldErrors } from '../../lib/catalog';
import { useSession } from '../../lib/session';
import type { PreparedPhoto } from '../../lib/image';
import { centsToPesosInput, fromInternalQty, pesosToCents, priceUnit, toInternalQty } from '../../lib/units';
import { PhotoPicker } from './PhotoPicker';

interface FormState {
  name: string;
  categoryId: string;
  unitType: UnitType;
  kind: ProductKind;
  animal: Animal;
  plu: string;
  sku: string;
  minStock: string;
  tracksExpiry: boolean;
  isActive: boolean;
  sellable: boolean;
  openPrice: boolean;
  shelfLife: string;
  price: string;
  vatRate: VatRate | '';
}

const ANIMAL_BY_ICON: Partial<Record<string, Animal>> = {
  beef: 'BEEF',
  pork: 'PORK',
  chicken: 'CHICKEN',
};

export function ProductDialog({
  product,
  categories,
  onClose,
  onSaved,
}: {
  product: ProductDTO | null;
  categories: CategoryDTO[];
  onClose: () => void;
  onSaved: (p: ProductDTO, isNew: boolean, warning?: string) => Promise<void>;
}) {
  const [form, setForm] = useState<FormState>(() => ({
    name: product?.name ?? '',
    categoryId: product?.categoryId ?? '',
    unitType: product?.unitType ?? 'WEIGHT',
    kind: product?.kind ?? 'MEAT',
    animal: product?.animal ?? 'BEEF',
    plu: product?.plu ?? '',
    sku: product?.sku ?? '',
    minStock: product && product.minStock > 0 ? fromInternalQty(product.unitType, product.minStock) : '',
    tracksExpiry: product?.tracksExpiry ?? true,
    isActive: product?.isActive ?? true,
    sellable: product?.sellable ?? true,
    openPrice: product?.openPrice ?? false,
    shelfLife: product?.shelfLifeDays?.toString() ?? '',
    vatRate: product?.vatRate ?? '',
    price: product
      ? (() => {
          const c = product.generalPriceCents ?? product.priceCents;
          return c !== null ? centsToPesosInput(c) : '';
        })()
      : '',
  }));
  const { me } = useSession();
  const fiscal = me.tenant.modules.includes('ARCA');
  // Solo bifi no lleva stock ni compras: sin stock mínimo ni vencimientos.
  const bifiOnly = me.tenant.plan === 'BIFI';
  const [photo, setPhoto] = useState<PreparedPhoto | null>(null);
  const [currentPhoto, setCurrentPhoto] = useState<string | null>(product?.photoUrl ?? null);
  const [removingPhoto, setRemovingPhoto] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((f) => ({ ...f, [key]: value }));
  const category = categories.find((c) => c.id === form.categoryId) ?? null;

  function onCategory(id: string) {
    const cat = categories.find((c) => c.id === id);
    setForm((f) => ({
      ...f,
      categoryId: id,
      // Sugerencia: la categoría "Cerdo" propone animal cerdo, etc. Se puede cambiar.
      animal: !product && cat && ANIMAL_BY_ICON[cat.icon] ? ANIMAL_BY_ICON[cat.icon]! : f.animal,
    }));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    const local: Record<string, string> = {};
    const minStock = form.minStock.trim() ? toInternalQty(form.unitType, form.minStock) : 0;
    if (minStock === null) local.minStock = 'Ingresá un número';
    const priceInput = form.price.trim() ? pesosToCents(form.price) : undefined;
    if (priceInput === null) local.price = 'Ingresá un importe válido';
    const priceCents = !product ? priceInput : undefined;
    // Al editar, el precio se cambia por su lado para que quede en el historial de precios.
    const currentPrice = product ? (product.generalPriceCents ?? product.priceCents) : null;
    const newPrice = product && priceInput !== undefined && priceInput !== null && priceInput !== currentPrice ? priceInput : null;

    const payload = {
      name: form.name,
      categoryId: form.categoryId || null,
      sku: form.sku,
      plu: form.plu,
      unitType: form.unitType,
      kind: form.kind,
      animal: form.animal,
      isCarcass: product?.isCarcass ?? false,
      minStock: minStock ?? 0,
      tracksExpiry: form.tracksExpiry,
      isActive: form.isActive,
      sellable: form.kind === 'SUPPLY' ? false : form.sellable,
      openPrice: form.openPrice,
      shelfLifeDays: form.shelfLife.trim() === '' ? null : Number(form.shelfLife),
      ...(fiscal ? { vatRate: form.vatRate || null } : {}),
      ...(priceCents !== undefined && priceCents !== null ? { priceCents } : {}),
    };
    const parsed = productSchema.safeParse(payload);
    const fieldErrors = {
      ...local,
      ...(parsed.success ? {} : zodFieldErrors(parsed.error)),
    };
    if (fieldErrors.priceCents) fieldErrors.price = fieldErrors.priceCents;
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) return;

    setSaving(true);
    try {
      let saved = product ? await api.patch<ProductDTO>(`/products/${product.id}`, payload) : await api.post<ProductDTO>('/products', payload);
      if (newPrice !== null) saved = await api.put<ProductDTO>(`/products/${saved.id}/price`, { priceCents: newPrice, branchId: null });
      let warning: string | undefined;
      if (photo) {
        try {
          saved = await uploadProductPhoto(saved.id, photo);
        } catch (err) {
          warning = `El producto se guardó, pero la foto no: ${errorMessage(err)}`;
        }
      }
      await onSaved(saved, !product, warning);
    } catch (err) {
      if (isApiError(err) && err.fields) setErrors(err.fields);
      else setFormError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  async function removePhoto() {
    if (!product) return;
    setRemovingPhoto(true);
    try {
      await api.del(`/products/${product.id}/photo`);
      setCurrentPhoto(null);
    } catch (err) {
      setFormError(errorMessage(err));
    } finally {
      setRemovingPhoto(false);
    }
  }

  const qtyUnit = form.unitType === 'WEIGHT' ? 'kg' : 'unidades';

  return (
    <Dialog open onOpenChange={(o) => !o && !saving && onClose()} title={product ? 'Editar producto' : 'Nuevo producto'} wide>
      <form className="bf-form" onSubmit={onSubmit} noValidate>
        {formError && <Callout tone="danger">{formError}</Callout>}

        <PhotoPicker
          currentUrl={currentPhoto}
          icon={category?.icon ?? product?.categoryIcon ?? null}
          name={form.name}
          pending={photo}
          onPick={setPhoto}
          onRemove={product ? () => void removePhoto() : undefined}
          busy={saving || removingPhoto}
        />

        <div className="bf-form-row">
          <Field label="Nombre" error={errors.name}>
            <Input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Asado de tira" autoFocus={!product} />
          </Field>
          <Field label="Categoría" error={errors.categoryId}>
            <Select value={form.categoryId} onChange={(e) => onCategory(e.target.value)}>
              <option value="">Sin categoría</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <div className="bf-form-row">
          <Field label="Se vende" error={errors.unitType} hint={product ? 'No se puede cambiar si ya tiene stock cargado.' : undefined}>
            <Select value={form.unitType} onChange={(e) => set('unitType', e.target.value as UnitType)}>
              {(Object.keys(UNIT_TYPE_LABELS) as UnitType[]).map((u) => (
                <option key={u} value={u}>
                  {UNIT_TYPE_LABELS[u]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Tipo" error={errors.kind}>
            <Select value={form.kind} onChange={(e) => set('kind', e.target.value as ProductKind)}>
              {PRODUCT_KINDS.map((k) => (
                <option key={k} value={k}>
                  {PRODUCT_KIND_LABELS[k]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Animal" error={errors.animal}>
            <Select value={form.animal} onChange={(e) => set('animal', e.target.value as Animal)}>
              {ANIMALS.map((a) => (
                <option key={a} value={a}>
                  {ANIMAL_LABELS[a]}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <div className="bf-form-row">
          {!product?.isCarcass && (
            <Field
              label={`Precio de venta (${priceUnit(form.unitType)})`}
              error={errors.price}
              hint={product ? 'Precio general. Si lo cambiás, queda en el historial de precios.' : 'Opcional. Es el precio general.'}
            >
              <Input inputMode="decimal" value={form.price} onChange={(e) => set('price', e.target.value)} placeholder="$ 0" />
            </Field>
          )}
          {fiscal && form.sellable && form.kind !== 'SUPPLY' && (
            <Field label="IVA" error={errors.vatRate} hint="Para la factura: el precio ya lo incluye. Consultá la alícuota con tu contador.">
              <Select value={form.vatRate} onChange={(e) => set('vatRate', e.target.value as VatRate | '')}>
                <option value="">Sin cargar</option>
                {VAT_RATES.map((r) => (
                  <option key={r} value={r}>
                    {VAT_RATE_LABELS[r]}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          <Field label="Código de balanza (PLU)" error={errors.plu} hint="El que imprime la balanza en la etiqueta.">
            <Input inputMode="numeric" value={form.plu} onChange={(e) => set('plu', e.target.value)} placeholder="101" />
          </Field>
          <Field label="Código interno o de barras" error={errors.sku}>
            <Input value={form.sku} onChange={(e) => set('sku', e.target.value)} placeholder="Opcional" />
          </Field>
        </div>

        {!bifiOnly && (
          <>
            <div className="bf-form-row">
              <Field label={`Stock mínimo (${qtyUnit})`} error={errors.minStock} hint="Debajo de esto te avisamos con una alerta.">
                <Input inputMode="decimal" value={form.minStock} onChange={(e) => set('minStock', e.target.value)} placeholder="0" />
              </Field>
            </div>

            <div className="bf-switch-row">
              <div>
                <strong>Controla vencimiento</strong>
                <small>Al cargar una compra se pide la fecha de vencimiento de cada lote.</small>
              </div>
              <Switch checked={form.tracksExpiry} onCheckedChange={(v) => set('tracksExpiry', v)} label="Controla vencimiento" />
            </div>
          </>
        )}
        {!bifiOnly && form.tracksExpiry && (
          <Field label="Días de vida (opcional)" hint="Vacío = los de su tipo (Configuración → Vencimientos).">
            <Input
              inputMode="numeric"
              value={form.shelfLife}
              onChange={(e) => set('shelfLife', e.target.value.replace(/\D/g, ''))}
              style={{ maxWidth: 120 }}
            />
          </Field>
        )}
        {form.kind === 'SUPPLY' ? (
          <p className="bf-hint">Los insumos se compran y se usan en Producción (recetas). No aparecen en el mostrador.</p>
        ) : (
          <>
            <div className="bf-switch-row">
              <div>
                <strong>Se vende en el mostrador</strong>
                <small>Apagalo para algo que solo usás en producción (ej.: huevos que no vendés sueltos).</small>
              </div>
              <Switch checked={form.sellable} onCheckedChange={(v) => set('sellable', v)} label="Se vende en el mostrador" />
            </div>
            {form.sellable && (
              <div className="bf-switch-row">
                <div>
                  <strong>Precio al momento</strong>
                  <small>
                    El precio se escribe al vender (ej.: la grasa, que paga el sebero ese día). Si preferís un valor fijo para el mes, dejalo apagado
                    y cargá el precio.
                  </small>
                </div>
                <Switch checked={form.openPrice} onCheckedChange={(v) => set('openPrice', v)} label="Precio al momento" />
              </div>
            )}
          </>
        )}
        <div className="bf-switch-row">
          <div>
            <strong>Activo</strong>
            <small>Los productos inactivos no aparecen para vender.</small>
          </div>
          <Switch checked={form.isActive} onCheckedChange={(v) => set('isActive', v)} label="Activo" />
        </div>

        <div className="bf-form-actions">
          <Button onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button type="submit" variant="primary" loading={saving}>
            {product ? 'Guardar cambios' : 'Crear producto'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
